// frontend/src/pages/Login.js
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import axios from "../lib/axios";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: setAuthSession } = useAuth();

  // page we came from (e.g. /consultancy/123)
  const from = location.state?.from || null;
  const requiredRole = location.state?.requiredRole || null;

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("password");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (!resendTimer) return;
    const timer = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendTimer]);

  const emailRegex = /\S+@\S+\.\S+/;

  const validateIdentifier = (emailOnly = false) => {
    const trimmed = identifier.trim();
    if (!trimmed) {
      setError(emailOnly ? "Please enter your email" : "Please enter your email or mobile number");
      return null;
    }
    if (emailOnly) {
      if (!emailRegex.test(trimmed)) {
        setError("Please enter a valid email");
        return null;
      }
      return trimmed;
    }

    const isEmail = emailRegex.test(trimmed);
    const isPhone = /^\d{8,}$/.test(trimmed);
    if (!isEmail && !isPhone) {
      setError("Enter a valid email address or mobile number");
      return null;
    }
    return trimmed;
  };

  const handleAuthSuccess = (data) => {
    if (!data || !data.user || !data.token) {
      setError("Unexpected response from server");
      return;
    }

    if (data.user.role === "consultancy" || data.user.role === "consultancy_owner") {
      data.user.role = "owner";
    }

    setAuthSession(data.user, data.token);

    if (from) {
      navigate(from, { replace: true });
      return;
    }

    if (data.user.role === "owner") {
      navigate("/owner/dashboard", { replace: true });
    } else if (data.user.role === "admin") {
      navigate("/admin", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  const login = async () => {
    setError("");
    setOtpMessage("");
    const validIdentifier = validateIdentifier();
    if (!validIdentifier) return;
    if (!password) {
      setError("Please enter your password");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post("/auth/login", {
        identifier: validIdentifier,
        password,
      });
      if (!data || data.error) {
        setError(data?.error || "Login failed");
        return;
      }

      handleAuthSuccess(data);
    } catch (err) {
      console.error("Login error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async () => {
    setError("");
    setOtpMessage("");
    const validIdentifier = validateIdentifier(true);
    if (!validIdentifier) return;

    try {
      setLoading(true);
      const { data } = await axios.post("/auth/request-otp", {
        identifier: validIdentifier,
      });
      if (data?.error) {
        setError(data.error);
        return;
      }
      setOtp("");
      setOtpSent(true);
      setOtpMessage("OTP sent to your email. It expires in 5 minutes.");
      setResendTimer(45);
    } catch (err) {
      console.error("requestOtp error:", err);
      const message =
        err?.response?.data?.error || err?.message || "Could not send OTP. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError("");
    const validIdentifier = validateIdentifier(true);
    if (!validIdentifier) return;
    if (!/^\d{6}$/.test(otp.trim())) {
      setError("Enter the 6-digit code sent to your email");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post("/auth/verify-otp", {
        identifier: validIdentifier,
        otp: otp.trim(),
      });
      if (data?.error) {
        setError(data.error || "Invalid code");
        return;
      }
      handleAuthSuccess(data);
    } catch (err) {
      console.error("verifyOtp error:", err);
      setError("Could not verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setOtp("");
    setOtpSent(false);
    setOtpMessage("");
    setResendTimer(0);
  };

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-10 sm:px-6 lg:grid-cols-[1fr_430px] lg:py-14">
          <div className="flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">
              Secure access
            </p>
            <h1 className="mt-3 max-w-3xl text-3xl font-semibold leading-tight text-slate-950 md:text-5xl">
              Continue your study abroad journey with AbroadJacks.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600">
              Sign in to access your dashboard, saved consultancies, enquiries, and application planning tools.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Students</p>
                <p className="mt-2 text-2xl font-semibold">12K+</p>
                <p className="mt-1 text-xs text-slate-500">Using AbroadJacks</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Support</p>
                <p className="mt-2 text-2xl font-semibold">24h</p>
                <p className="mt-1 text-xs text-slate-500">Typical response window</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Access</p>
                <p className="mt-2 text-2xl font-semibold">Secure</p>
                <p className="mt-1 text-xs text-slate-500">Password or OTP login</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Login</p>
              <h2 className="text-2xl font-semibold text-slate-950">Welcome back</h2>
              <p className="text-sm text-slate-500">Use your password or a secure email OTP.</p>
            </div>

          {requiredRole && (
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span aria-hidden>⚠</span>
              <span>
                Please login as <span className="font-semibold">{requiredRole}</span> to access that page.
              </span>
            </div>
          )}

          {error && (
            <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <span aria-hidden>✕</span>
              <span>{error}</span>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-slate-100 p-1 text-sm font-semibold">
            <button
              type="button"
              onClick={() => switchMode("password")}
              className={`rounded-md py-2 transition ${
                mode === "password" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-950"
              }`}
            >
              Password Login
            </button>
            <button
              type="button"
              onClick={() => switchMode("otp")}
              className={`rounded-md py-2 transition ${
                mode === "otp" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-950"
              }`}
            >
              OTP Login
            </button>
          </div>

          <label className="mt-6 block text-sm font-medium text-slate-700">
            {mode === "otp" ? "Email" : "Email or Mobile Number"}
          </label>
          <input
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
            type={mode === "otp" ? "email" : "text"}
            placeholder={mode === "otp" ? "Enter your email" : "name@domain.com or 9876543210"}
            value={identifier}
            onChange={(e) => {
              setIdentifier(e.target.value);
              if (mode === "otp" && error) {
                setError("");
              }
            }}
          />

          {mode === "password" ? (
            <>
              <label className="mt-4 block text-sm font-medium text-slate-700">Password</label>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="mt-2 text-right text-sm text-cyan-700 hover:text-cyan-800">
                <button type="button" onClick={() => navigate("/forgot-password")}>
                  Forgot Password?
                </button>
              </div>

              <button
                type="button"
                className="mt-6 w-full rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                onClick={login}
                disabled={loading}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {loading && <span className="aj-spinner" aria-hidden />}
                  {loading ? "Please wait..." : "Login securely"}
                </span>
              </button>
              <p className="mt-2 text-center text-[11px] text-slate-500">
                Protected with encrypted authentication
              </p>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                We&apos;ll send a secure 6-digit login code to your registered email.
              </p>
              <button
                type="button"
                className="mt-4 w-full rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                onClick={requestOtp}
                disabled={loading || (resendTimer > 0 && otpSent)}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {loading && <span className="aj-spinner" aria-hidden />}
                  {otpSent ? `Resend OTP${resendTimer ? ` (${resendTimer}s)` : ""}` : "Send OTP"}
                </span>
              </button>

              {otpSent && (
                <>
                  <label className="mt-6 block text-sm font-medium text-slate-700">
                    Enter OTP
                  </label>
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-center tracking-[6px] text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 sm:tracking-[8px]"
                    placeholder="123456"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  />
                  {otpMessage && (
                    <p className="mt-2 flex items-center gap-2 text-xs text-emerald-700">
                      <span aria-hidden>✓</span>
                      <span>{otpMessage}</span>
                    </p>
                  )}

                  <button
                    type="button"
                    className="mt-4 w-full rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                    onClick={verifyOtp}
                    disabled={loading}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      {loading && <span className="aj-spinner" aria-hidden />}
                      {loading ? "Verifying..." : "Verify & Continue"}
                    </span>
                  </button>
                  <p className="mt-2 text-center text-[11px] text-slate-500">
                    Protected with encrypted authentication
                  </p>
                </>
              )}
            </>
          )}

          <p className="mt-6 text-center text-sm text-slate-600">
            Don’t have an account?{" "}
            <button
              type="button"
              className="font-semibold text-slate-950 underline-offset-2 hover:underline"
              onClick={() => navigate("/register")}
            >
              Register now
            </button>
          </p>

          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Trusted locally</p>
            <p className="mt-1 text-sm text-slate-700">
              Trusted by students across AP & Telangana
              <span className="ml-2 inline-flex items-center rounded-md border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[11px] font-semibold text-cyan-700">
                12K+ users
              </span>
            </p>
          </div>
        </div>
      </div>
      </section>
    </main>
  );
}
